import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import BulkTicketSaleBatchDetails from 'Services/SalesManagement/Features/TicketSale/Components/BulkTicketSaleBatchDetails'
import BulkTicketSaleBatchDialog from 'Services/SalesManagement/Features/TicketSale/Components/BulkTicketSaleBatchDialog'
import BulkTicketSaleDetails from 'Services/SalesManagement/Features/TicketSale/Components/BulkTicketSaleDetails'
import { useBulkTicketSaleDrawerFunctions } from 'Services/SalesManagement/Features/TicketSale/Hooks/useBulkTicketSaleDrawerFunctions'
import { BulkTicketSaleTabType } from 'Services/SalesManagement/Features/TicketSale/types'
import { isBatchCancellable } from 'Services/SalesManagement/Features/TicketSale/utils'
import { DRAWER_TAB_PARAM_KEY } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import CancelIcon from '@mui/icons-material/Cancel'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider } from '@mui/material'
import Tab from '@mui/material/Tab'
import { type SyntheticEvent, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

function handleCancelResults(results: PromiseSettledResult<unknown>[]) {
  if (results.some(r => r.status === 'rejected')) {
    SnackbarUtils.error(t`Failed to cancel some batches.`)
  } else {
    SnackbarUtils.success(t`Ticket cancellation successfully started.`)
  }
}

interface BulkTicketSaleDrawerProps {
  open: boolean
  handleClose: () => void
  selectedBulkTicketSaleId: string | undefined
}

export default function BulkTicketSaleDrawer({
  open,
  handleClose,
  selectedBulkTicketSaleId
}: Readonly<BulkTicketSaleDrawerProps>) {
  const {
    selectedBulkTicketSale,
    isFetching,
    batches,
    cancelBulkTicketSaleBatch,
    createBulkTicketSaleBatch,
    isCreateLoading
  } = useBulkTicketSaleDrawerFunctions(selectedBulkTicketSaleId)
  const { confirm } = useConfirm()
  const [isCancelling, setIsCancelling] = useState(false)
  const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get(DRAWER_TAB_PARAM_KEY) as BulkTicketSaleTabType | null
  const allowedTabs = useMemo(() => Object.values(BulkTicketSaleTabType), [])
  const tabValue = rawTab && allowedTabs.includes(rawTab) ? rawTab : BulkTicketSaleTabType.DETAILS

  const changeTab = (newTab: BulkTicketSaleTabType) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set(DRAWER_TAB_PARAM_KEY, newTab)
    setSearchParams(newParams)
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: BulkTicketSaleTabType) => {
    changeTab(newValue)
  }

  const handleCreateBatch = (amount: number) => {
    if (!selectedBulkTicketSale) {
      SnackbarUtils.error(t`No bulk ticket sale selected.`)
      return
    }
    createBulkTicketSaleBatch({
      bulkSaleId: selectedBulkTicketSale.id,
      requested_quantity: amount
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Tickets created successfully.`)
        setIsBatchDialogOpen(false)
      })
      .catch(() => SnackbarUtils.error(t`There was an error creating the tickets.`))
  }

  const handleCancelBatch = () => {
    if (!selectedBulkTicketSale || !batches) return
    const cancellableBatches = batches.filter(batch => isBatchCancellable(batch.status))
    if (cancellableBatches.length === 0) {
      SnackbarUtils.error(t`All batches have already been cancelled.`)
      return
    }
    const total = selectedBulkTicketSale.total_requested_quantity

    const cancelBatches = () => {
      setIsCancelling(true)
      Promise.allSettled(
        cancellableBatches.map(batch =>
          cancelBulkTicketSaleBatch({
            bulkSaleId: selectedBulkTicketSale.id,
            batchId: batch.id
          }).unwrap()
        )
      )
        .then(result => {
          handleCancelResults(result)
          changeTab(BulkTicketSaleTabType.DOWNLOAD)
        })
        .finally(() => setIsCancelling(false))
    }

    confirm({
      title: t`Cancel tickets`,
      message: t`If you cancel the tickets, all tickets in this bulk sale will be invalidated.\n\nTickets in total: ${total} pcs`,
      handleConfirm: cancelBatches
    })
  }

  return (
    <DrawerRight open={open} onClose={handleClose}>
      {selectedBulkTicketSale && (
        <>
          <BulkTicketSaleBatchDialog
            open={isBatchDialogOpen}
            handleClose={() => setIsBatchDialogOpen(false)}
            handleSubmit={handleCreateBatch}
            isLoading={isCreateLoading}
            title={t`Sell more tickets`}
            eventIdentifier={selectedBulkTicketSale.event_identification ?? ''}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pl: 1 }}>
            {batches && batches.length > 0 && (
              <CustomLoadingButton
                size="small"
                isLoading={isCancelling}
                disabled={isCancelling}
                variant="text"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancelBatch}
              >
                {t`Cancel tickets`}
              </CustomLoadingButton>
            )}
            <CustomButton
              size="small"
              variant="contained"
              onClick={() => setIsBatchDialogOpen(true)}
            >{t`Sell more tickets`}</CustomButton>
          </Box>
          <WithLoader loading={isFetching}>
            <TabContext value={tabValue}>
              <TabList onChange={handleTabChange} aria-label={t`Bulk ticket sale tab list`}>
                <Tab label={t`Details`} value={BulkTicketSaleTabType.DETAILS} />
                <Tab label={t`Download tickets`} value={BulkTicketSaleTabType.DOWNLOAD} />
              </TabList>
              <Divider />
              <TabPanel sx={{ p: 0 }} value={BulkTicketSaleTabType.DETAILS}>
                <BulkTicketSaleDetails bulkTicketSale={selectedBulkTicketSale} />
              </TabPanel>
              <TabPanel sx={{ p: 0 }} value={BulkTicketSaleTabType.DOWNLOAD}>
                <BulkTicketSaleBatchDetails bulkSaleId={selectedBulkTicketSale.id} />
              </TabPanel>
            </TabContext>
          </WithLoader>
        </>
      )}
    </DrawerRight>
  )
}
